import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Button, Col, FlexboxGrid } from 'rsuite';
import dayjs from 'dayjs';
import api from '../../../api/apiAccess';
import { DASHBOARD_ACTIONS_ENUM } from '../../../common/enums';
import {
  buildSingleLicenseTaxPeriod,
  expandQuarterlyPeriods,
} from '../../../common/taxFlowUtils';
import { selectConfigTaxPaymentPeriod } from '../../../store/slices/config/configSelectors';
import { selectCurrentIDToken } from '../../../store/slices/auth/authSelectors';
import {
  setPreSelectedTaxPeriod,
  updateTaxFlowData,
} from '../../../store/slices/tax/taxSlice';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { setTaxSteps } from '../../../store/slices/flow/flowThunks';
import {
  selectInitiatingRenewFlow,
  selectInitiatingRenewFlowError,
  selectRenewApplicationNumber,
} from '../../../store/slices/renew/renewSelectors';
import {
  initiateBulkRenewFlow,
  initiateRenewFlow,
} from '../../../store/slices/renew/renewThunks';
import { isNullUndefinedOrEmptyString } from '../../../common/utils';
import { useAppDispatch } from 'src/store/store';
import IconIndicator from '../../user-feedback/IconIndicator';
import Notice from '../../user-feedback/Notice';

export interface ActionItemSelectedLicense {
  address?: string;
  licenseNumber?: string;
  applicationNumber?: string;
}

export interface ActionItemAction {
  label: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  type: DASHBOARD_ACTIONS_ENUM | string;
  periodLabel?: string;
  selectedLicense?: ActionItemSelectedLicense | null;
}

interface ActionItemProps {
  action: ActionItemAction;
}

const ActionItem = ({ action }: ActionItemProps) => {
  const {
    type,
    label,
    dueDate,
    startDate,
    endDate,
    periodLabel,
    selectedLicense,
  } = action;

  const {
    address = '',
    licenseNumber = '',
    applicationNumber = '',
  } = selectedLicense || {};

  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();

  const [showInitiateRenewFlowError, setShowInitiateRenewFlowError] =
    useState(false);
  const initiatingRenewFlow = useSelector(selectInitiatingRenewFlow);
  const initiatingRenewFlowError = useSelector(selectInitiatingRenewFlowError);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const applicationBeingRenewed = useSelector(selectRenewApplicationNumber);
  const idToken = useSelector(selectCurrentIDToken);
  const taxPaymentPeriod = useSelector(selectConfigTaxPaymentPeriod);

  useEffect(() => {
    if (
      !initiatingRenewFlow &&
      applicationNumber === applicationBeingRenewed &&
      type === DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW &&
      !isNullUndefinedOrEmptyString(initiatingRenewFlowError)
    ) {
      setShowInitiateRenewFlowError(true);
      setTimeout(() => {
        setShowInitiateRenewFlowError(false);
      }, 10000);
    } else {
      setShowInitiateRenewFlowError(false);
    }
  }, [
    initiatingRenewFlow,
    applicationBeingRenewed,
    applicationNumber,
    initiatingRenewFlowError,
    type,
  ]);

  useEffect(() => {
    if (type === DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW) {
      setShowLoadingIndicator(
        initiatingRenewFlow && applicationNumber === applicationBeingRenewed
      );
    }
  }, [initiatingRenewFlow, applicationBeingRenewed, applicationNumber, type]);

  const actionButtonText = useMemo(() => {
    switch (type) {
      case DASHBOARD_ACTIONS_ENUM.BULK_TAX:
        return 'Pay Tax';
      case DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW:
        return 'Renew';
      case DASHBOARD_ACTIONS_ENUM.BULK_RENEW:
        return 'Bulk Renew';
      default:
        return '';
    }
  }, [type]);

  const onFlowStepsMapped = useCallback(
    (path?: string) => {
      placeNavigate(`/${path}`);
    },
    [placeNavigate]
  );

  const bulkTaxClick = useCallback(async () => {
    if (selectedLicense) {
      const period = buildSingleLicenseTaxPeriod({
        address,
        applicationNumber,
        endDate: endDate ?? '',
        label: periodLabel ?? '',
        licenseNumber,
        startDate: startDate ?? '',
      });
      const lookupLicense = await api.getLookupLicenseNoStateASync(
        selectedLicense.licenseNumber ?? '',
        idToken
      );

      const selectedLicenses = expandQuarterlyPeriods(
        [period],
        lookupLicense?.data.totPaymentPeriod,
        taxPaymentPeriod
      );
      dispatch(updateTaxFlowData({ selectedLicenses }));
      dispatch(setTaxSteps(onFlowStepsMapped, true, `/dashboard`));
    } else {
      dispatch(
        setPreSelectedTaxPeriod({
          startDate: startDate ?? '',
          endDate: endDate ?? '',
        })
      );
      dispatch(setTaxSteps(onFlowStepsMapped, false));
    }
  }, [
    address,
    applicationNumber,
    dispatch,
    endDate,
    idToken,
    licenseNumber,
    onFlowStepsMapped,
    periodLabel,
    selectedLicense,
    startDate,
    taxPaymentPeriod,
  ]);

  const singleRenewClick = useCallback(() => {
    if (!initiatingRenewFlow) {
      dispatch(
        initiateRenewFlow(applicationNumber, `/dashboard`, onFlowStepsMapped)
      );
    }
  }, [applicationNumber, dispatch, initiatingRenewFlow, onFlowStepsMapped]);

  const bulkRenewClick = useCallback(() => {
    if (!initiatingRenewFlow) {
      dispatch(initiateBulkRenewFlow(`/dashboard`, onFlowStepsMapped));
    }
  }, [dispatch, initiatingRenewFlow, onFlowStepsMapped]);

  const actionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.blur();
      setShowLoadingIndicator(true);
      switch (type) {
        case DASHBOARD_ACTIONS_ENUM.BULK_TAX:
          void bulkTaxClick();
          break;
        case DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW:
          singleRenewClick();
          break;
        case DASHBOARD_ACTIONS_ENUM.BULK_RENEW:
          bulkRenewClick();
          break;
      }
    },
    [bulkRenewClick, bulkTaxClick, singleRenewClick, type]
  );

  return (
    <div role="grid">
      <FlexboxGrid
        align={'middle' as const}
        className="pv-12 pl-20"
        style={{ paddingRight: '13px' }}
      >
        <FlexboxGrid.Item
          as={Col}
          colspan={24}
          lg={16}
        >
          <Text>{label}</Text>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as={Col}
          colspan={24}
          lg={4}
        >
          <Text>{dayjs(dueDate).format('MM/DD/YYYY')}</Text>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as={Col}
          colspan={24}
          lg={4}
        >
          <Button
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={actionClick}
          >
            {actionButtonText}
            {showLoadingIndicator && (
              <span style={{ marginLeft: '5px', display: 'inline-flex' }}>
                <IconIndicator
                  size={16}
                  color="#FFFFFF"
                />
              </span>
            )}
          </Button>
        </FlexboxGrid.Item>
        {showInitiateRenewFlowError && (
          <FlexboxGrid.Item
            as={Col}
            xs={24}
            style={{ marginTop: '10px' }}
          >
            <Notice message={initiatingRenewFlowError} />
          </FlexboxGrid.Item>
        )}
      </FlexboxGrid>
    </div>
  );
};

export default ActionItem;
